<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3 hide-on-print">
      <SimplePageHeader title="Commissions Details" class="px-4 pr-5 mb-5">
        <template v-slot:right-buttons>
          <ButtonPrint
            :block="true"
          />
        </template>
      </SimplePageHeader>
      <div v-if="showFilters" class="px-5 mb-sm-5">
        <v-row align="center">
          <v-col cols="12" sm="4" md="3" lg="2">
            <MonthPicker
              label="Month"
              :year="year"
              :month="month"
              @yearChange="(newValue) => this.year = newValue"
              @monthChange="(newValue) => this.month = newValue"
            ></MonthPicker>
          </v-col>
        </v-row>
      </div>
    </div>
    <ReportHeader
      reportTitle="Commission Details Report"
      :reportCriteria="reportFilterCriteria"
    ></ReportHeader>
    <div v-if="!showNoDataMessage" class="mt-5">
      <v-row v-if="employeeName">
        <v-col cols="12">
          <v-card>
            <v-card-text>
              <CommissionDetailsReportHeader
                :userName="employeeName"
                :commissionAmount="totalCommission"
                :individualGp="totalIndividualGP"
                :year="year"
                :month="month"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row v-if="directPlacementGrossProfitItems && directPlacementGrossProfitItems.length > 0">
        <v-col cols="12">
          <v-card>
            <v-card-text>
              <DirectPlacementCommissions
                class="px-4 pt-5"
                :directPlacementCommissions="directPlacementGrossProfitItems"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row v-if="assignmentGrossProfitItems && assignmentGrossProfitItems.length > 0">
        <v-col cols="12">
          <v-card>
            <v-card-text>
              <AssignmentsGrossProfits
                class="px-4 pt-5"
                :assignmentGrossProfitItems="assignmentGrossProfitItems"
                :month="month"
                :year="year"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row v-if="projectAssignmentGrossProfitItems && projectAssignmentGrossProfitItems.length > 0">
        <v-col cols="12">
          <v-card>
            <v-card-text>
              <ProjectGrossProfits
                class="px-4 pt-5"
                :projectAssignmentGrossProfitItems="projectAssignmentGrossProfitItems"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row v-if="teamMembersGrossProfitItems && teamMembersGrossProfitItems.length > 0">
        <v-col cols="12">
          <v-card>
            <v-card-text>
              <TeamMembersGrossProfitTable
                class="px-4 pt-5"
                :commissionOverridePercentage="commissionOverridePercentage"
                :teamMemberGrossProfitItems="teamMembersGrossProfitItems"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row v-if="commissionTiers && commissionTiers.length > 0">
        <v-col cols="12">
          <v-card>
            <v-card-text>
              <CommissionTiers
                class="px-4 pt-5"
                :commissionTiers="commissionTiers"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
    <div class="mt-5" v-if="showNoDataMessage">
      <NoDataAvailableMessage></NoDataAvailableMessage>
    </div>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { ButtonPrint, BottomSpacer } from '@/components/buttons'
import { MonthPicker } from '@/components/shared/datePickers'
import CommissionDetailsReportHeader from './CommissionDetailsReportHeader'
import DirectPlacementCommissions from './DirectPlacementCommissions'
import AssignmentsGrossProfits from './AssignmentsGrossProfits'
import ProjectGrossProfits from './ProjectGrossProfits'
import CommissionTiers from './CommissionTiers'
import { SimplePageHeader } from '@/components/pageComponents'
import { ADMIN, MANAGER } from '@/lib/constants/securityRoles'
import { getCurrentUser, userHasRoles } from '@/lib/authentication'
import { NoDataAvailableMessage } from '@/components/reports'
import TeamMembersGrossProfitTable from './TeamMemberGrossProfitTable'
import ReportHeader from '@/components/reports/ReportHeader'

export default {
  components: {
    ButtonPrint,
    BottomSpacer,
    MonthPicker,
    CommissionDetailsReportHeader,
    DirectPlacementCommissions,
    AssignmentsGrossProfits,
    ProjectGrossProfits,
    CommissionTiers,
    SimplePageHeader,
    NoDataAvailableMessage,
    TeamMembersGrossProfitTable,
    ReportHeader
  },
  data () {
    return {
      showNoDataMessage: true,
      isInitialized: false
    }
  },
  computed: {
    ...mapFields('commissionDetailsReport', [
      'employeeName',
      'assignmentGrossProfitItems',
      'projectAssignmentGrossProfitItems',
      'directPlacementGrossProfitItems',
      'teamMembersGrossProfitItems',
      'commissionTiers',
      'consultantsGP',
      'projectsGP',
      'permPlacementGP',
      'totalIndividualGP',
      'commissionOverride',
      'commissionOverridePercentage',
      'totalCommission'
    ]),
    ...mapFields('commissionDetailsReportFilters', [
      'userId',
      'month',
      'year',
      'businessEntityId'
    ]),
    reportFilterCriteria: {
      get () {
        let months = [ 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December' ]
        return [
          { 'key': 'Month', 'value': months[parseInt(this.month) - 1] + ' - ' + parseInt(this.year) }
        ]
      }
    },
    userIdURLParameter () {
      return this.$route.query.user
    },
    yearURLParameter () {
      return this.$route.query.year
    },
    monthURLParameter () {
      return this.$route.query.month
    },
    businessEntityIdURLParameter () {
      return this.$route.query.businessEntityId
    },
    showFilters () {
      return !this.userHasRoles({ user: this.getCurrentUser(), allowedRoles: [ADMIN, MANAGER] })
    },
    monthYearFilter () {
      return { month: this.month, year: this.year }
    }
  },
  watch: {
    monthYearFilter () {
      if (this.isInitialized) {
        this.handleSearch()
      }
    }
  },
  methods: {
    userHasRoles,
    getCurrentUser,
    ...mapActions('commissionDetailsReport', [
      'getCommissionDetailsReportWithFilters'
    ]),
    handleSearch () {
      this.getCommissionDetailsReportWithFilters().then(() => {
        if (this.userId) {
          this.showNoDataMessage = !this.employeeName
        } else {
          this.showNoDataMessage = true
        }
      })
    }
  },
  mounted () {
    if (this.userIdURLParameter) {
      this.userId = parseInt(this.userIdURLParameter)
    } else {
      this.userId = getCurrentUser().id
    }

    if (this.yearURLParameter) {
      this.year = this.yearURLParameter
    }
    if (this.monthURLParameter) {
      this.month = this.monthURLParameter
    }
    if (this.businessEntityIdURLParameter) {
      this.businessEntityId = parseInt(this.businessEntityIdURLParameter)
    }

    if (this.userId && this.year && this.month) {
      this.handleSearch()
    } else {
      this.showNoDataMessage = true
    }
    this.$nextTick(() => {
      this.isInitialized = true
    })
  }
}
</script>
